'use client';

import { useEffect, useRef, useState } from 'react';
import Image, { type ImageProps } from 'next/image';
import { clsx } from 'clsx';

/**
 * Subtle pulsing placeholder. Absolutely fills its (relative, sized) parent so it
 * matches the final media's box exactly — no layout shift. Sits behind the media,
 * which fades in over it on load.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={clsx(
        'pointer-events-none absolute inset-0 animate-pulse bg-surface-container-high',
        className
      )}
    />
  );
}

function fadeClass(loaded: boolean, className?: string) {
  return clsx('transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0', className);
}

/** next/image with a skeleton placeholder that fades into the image on load.
 *  `onReady` fires when the image is actually shown (loaded, errored, or already
 *  cached) — lets parents gate behavior on it (e.g. the compare slider's intro
 *  sweep). May be called more than once; keep it idempotent. */
export function SmartImage({
  className,
  onReady,
  ...props
}: ImageProps & { onReady?: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement>(null);

  // If the image is already complete by the time we mount (cached / decoded
  // before the onLoad listener attached), reveal it immediately.
  useEffect(() => {
    if (ref.current?.complete) setLoaded(true);
  }, []);

  // Notify the parent at the same moment the skeleton lifts.
  useEffect(() => {
    if (loaded) onReady?.();
  }, [loaded, onReady]);

  return (
    <>
      {!loaded && <Skeleton />}
      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt is provided by callers via props */}
      <Image
        ref={ref}
        {...props}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={fadeClass(loaded, className)}
      />
    </>
  );
}

/** Plain <img> with a skeleton placeholder that fades into the image on load. */
export function SmartImg({ className, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <>
      {!loaded && <Skeleton />}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        ref={ref}
        {...props}
        alt={props.alt ?? ''}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={fadeClass(loaded, className)}
      />
    </>
  );
}

/** Muted, non-autoplaying <video> poster with a skeleton until the first frame. */
export function SmartVideoPoster({
  className,
  ...props
}: React.VideoHTMLAttributes<HTMLVideoElement>) {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // readyState >= 2 (HAVE_CURRENT_DATA) means the first frame is already available.
    if (ref.current && ref.current.readyState >= 2) setLoaded(true);
  }, []);

  return (
    <>
      {!loaded && (
        <>
          <Skeleton />
          {/* Spinner over the skeleton so a loading clip reads as "loading", not a
              blank dark frame (e.g. when navigating to the next carousel video). */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center"
          >
            <svg className="h-9 w-9 animate-spin text-on-surface/70" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4z" />
            </svg>
          </span>
        </>
      )}
      <video
        ref={ref}
        {...props}
        muted
        playsInline
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={fadeClass(loaded, className)}
      />
    </>
  );
}
